import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import styles from './AdditionalProceduresForm.module.scss';
import RadioButtonGroup from "../../../../components/radioButtonGroup/RadioButtonGroup";
import TextField from "../../../../components/textField/TextField";
import PropTypes from "prop-types";

const AdditionalProceduresForm = forwardRef(({
  formData,
  onSave,
  disabled = false
}, ref) => {
  const [ogNGTubePlaced, setOgNGTubePlaced] = useState(null);
  const [ogNGTubePlacedError, setOgNGTubePlacedError] = useState('');
  const [wasUltrasoundUsed, setWasUltrasoundUsed] = useState(null);
  const [wasUltrasoundUsedError, setWasUltrasoundUsedError] = useState('');
  const [numberOfProceduresTheUltrasoundWasUsedFor, setNumberOfProceduresTheUltrasoundWasUsedFor] = useState(null);
  const [numberOfProceduresTheUltrasoundWasUsedForError, setNumberOfProceduresTheUltrasoundWasUsedForError] = useState('');
  const [advancedNonInvasiveMonitoring, setAdvancedNonInvasiveMonitoring] = useState(null);
  const [advancedNonInvasiveMonitoringError, setAdvancedNonInvasiveMonitoringError] = useState('');
  const [assessmentOfChestXRay, setAssessmentOfChestXRay] = useState(null);
  const [assessmentOfChestXRayError, setAssessmentOfChestXRayError] = useState('');
  const [postoperativeAssessmentOrExam, setPostoperativeAssessmentOrExam] = useState(null);
  const [postoperativeAssessmentOrExamError, setPostoperativeAssessmentOrExamError] = useState('');
  const [preoperativeAssessmentOrExamIncludingHorP, setPreoperativeAssessmentOrExamIncludingHorP] = useState(null);
  const [preoperativeAssessmentOrExamIncludingHorPError, setPreoperativeAssessmentOrExamIncludingHorPError] = useState('');

  useEffect(() => {
    setOgNGTubePlaced(formData?.ogNGTubePlaced)
    setWasUltrasoundUsed(formData?.wasUltrasoundUsed)
    setNumberOfProceduresTheUltrasoundWasUsedFor(formData?.numberOfProceduresTheUltrasoundWasUsedFor || null)
    setAdvancedNonInvasiveMonitoring(formData?.advancedNonInvasiveMonitoring)
    setAssessmentOfChestXRay(formData?.assessmentOfChestXRay)
    setPostoperativeAssessmentOrExam(formData?.postoperativeAssessmentOrExam)
    setPreoperativeAssessmentOrExamIncludingHorP(formData?.preoperativeAssessmentOrExamIncludingHorP)
  }, [formData]);

  const validateOgNGTubePlacedError = () => {
    if(ogNGTubePlaced === null) {
      return 'OG/NG Tube Placed is required';
    }
    return '';
  }

  const validateWasUltrasoundUsedError = () => {
    if(wasUltrasoundUsed === null) {
      return 'Was Ultrasound Used is required';
    }
    return '';
  }

  const validateNumberOfProceduresTheUltrasoundWasUsedForError = () => {
    if(wasUltrasoundUsed && !numberOfProceduresTheUltrasoundWasUsedFor) {
      return 'Number of procedures the ultrasound was used for is required';
    }
    return '';
  }

  const validateAdvancedNonInvasiveMonitoringError = () => {
    if(advancedNonInvasiveMonitoring === null) {
      return 'Advanced Noninvasive Monitoring is required';
    }
    return '';
  }

  const validateAssessmentOfChestXRayError = () => {
    if(assessmentOfChestXRay === null) {
      return 'Assessment of Chest X-Ray is required';
    }
    return '';
  }

  const validatePostoperativeAssessmentOrExamError = () => {
    if(postoperativeAssessmentOrExam === null) {
      return 'Postoperative Assessment/Exam is required';
    }
    return '';
  }

  const validatePreoperativeAssessmentOrExamIncludingHorPError = () => {
    if(preoperativeAssessmentOrExamIncludingHorP === null) {
      return 'Preoperative Assessment/Exam (including H&P) is required';
    }
    return '';
  }

  const validateForm = () => {
    const ogNGTubePlacedValidationResult = validateOgNGTubePlacedError();
    const wasUltrasoundUsedValidationResult = validateWasUltrasoundUsedError();
    const numberOfProceduresTheUltrasoundWasUsedForValidationResult = validateNumberOfProceduresTheUltrasoundWasUsedForError();
    const advancedNonInvasiveMonitoringValidationResult = validateAdvancedNonInvasiveMonitoringError();
    const assessmentOfChestXRayValidationResult = validateAssessmentOfChestXRayError();
    const postoperativeAssessmentOrExamValidationResult = validatePostoperativeAssessmentOrExamError();
    const preoperativeAssessmentOrExamIncludingHorPValidationResult = validatePreoperativeAssessmentOrExamIncludingHorPError();

    setOgNGTubePlacedError(ogNGTubePlacedValidationResult);
    setWasUltrasoundUsedError(wasUltrasoundUsedValidationResult);
    setNumberOfProceduresTheUltrasoundWasUsedForError(numberOfProceduresTheUltrasoundWasUsedForValidationResult);
    setAdvancedNonInvasiveMonitoringError(advancedNonInvasiveMonitoringValidationResult);
    setAssessmentOfChestXRayError(assessmentOfChestXRayValidationResult);
    setPostoperativeAssessmentOrExamError(postoperativeAssessmentOrExamValidationResult);
    setPreoperativeAssessmentOrExamIncludingHorPError(preoperativeAssessmentOrExamIncludingHorPValidationResult);

    const errors = [
      ogNGTubePlacedValidationResult,
      wasUltrasoundUsedValidationResult,
      numberOfProceduresTheUltrasoundWasUsedForValidationResult,
      advancedNonInvasiveMonitoringValidationResult,
      assessmentOfChestXRayValidationResult,
      postoperativeAssessmentOrExamValidationResult,
      preoperativeAssessmentOrExamIncludingHorPValidationResult
    ];
  
    const errorCount = errors.filter(error => error !== '').length;
  
    return errorCount;
  };

  // Expose the function to the parent
  useImperativeHandle(ref, () => ({
    validateForm
  }));
  
  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <div className={styles.row}>
        <div className={styles.column30}>
          <RadioButtonGroup
            label="OG/NG Tube Placed"
            options={[
              {
                label: 'Yes',
                value: true
              },
              {
                label: 'No',
                value: false
              }
            ]}
            selected={ogNGTubePlaced}
            onChange={(value) => {
              setOgNGTubePlaced(value);
              onSave({
                ogNGTubePlaced: value
              })
            }}
            error={ogNGTubePlacedError}
            disabled={disabled}
          />
        </div>
        <div className={styles.column30}>
          <RadioButtonGroup
            label="Was Ultrasound Used?"
            options={[
              {
                label: 'Yes',
                value: true
              },
              {
                label: 'No',
                value: false
              }
            ]}
            selected={wasUltrasoundUsed}
            onChange={(value) => {
              setWasUltrasoundUsed(value);
              onSave(value ? {
                wasUltrasoundUsed: value
              } : {
                wasUltrasoundUsed: value,
                numberOfProceduresTheUltrasoundWasUsedFor: null
              });
            }}
            error={wasUltrasoundUsedError}
            disabled={disabled}
          />
        </div>
        {wasUltrasoundUsed === true && <div className={styles.column30}>
          <TextField
            label="Number of procedures the ultrasound was used for "
            type="number"
            value={numberOfProceduresTheUltrasoundWasUsedFor}
            onChange={(value) => setNumberOfProceduresTheUltrasoundWasUsedFor(value)}
            onBlur={() => {
              onSave({
                numberOfProceduresTheUltrasoundWasUsedFor: numberOfProceduresTheUltrasoundWasUsedFor
              });
            }}
            error={numberOfProceduresTheUltrasoundWasUsedForError}
            disabled={disabled}
          />
        </div>}
      </div>
      <div className={styles.row}>
        <div className={styles.column30}>
          <RadioButtonGroup
            label="Advanced Noninvasive Monitoring"
            options={[
              {
                label: 'Yes',
                value: true
              },
              {
                label: 'No',
                value: false
              }
            ]}
            selected={advancedNonInvasiveMonitoring}
            onChange={(value) => {
              setAdvancedNonInvasiveMonitoring(value);
              onSave({
                advancedNonInvasiveMonitoring: value
              });
            }}
            error={advancedNonInvasiveMonitoringError}
            disabled={disabled}
          />
        </div>
        <div className={styles.column30}>
          <RadioButtonGroup
            label="Assessment of Chest X-Ray"
            options={[
              {
                label: 'Yes',
                value: true
              },
              {
                label: 'No',
                value: false
              }
            ]}
            selected={assessmentOfChestXRay}
            onChange={(value) => {
              setAssessmentOfChestXRay(value);
              onSave({
                assessmentOfChestXRay: value
              });
            }}
            error={assessmentOfChestXRayError}
            disabled={disabled}
          />
        </div>
        <div className={styles.column30}>
          <RadioButtonGroup
            label="Postoperative Assessment/Exam"
            options={[
              {
                label: 'Yes',
                value: true
              },
              {
                label: 'No',
                value: false
              }
            ]}
            selected={postoperativeAssessmentOrExam}
            onChange={(value) => {
              setPostoperativeAssessmentOrExam(value);
              onSave({
                postoperativeAssessmentOrExam: value
              });
            }}
            error={postoperativeAssessmentOrExamError}
            disabled={disabled}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column30}>
          <RadioButtonGroup
            label="Preoperative Assessment/Exam (including H&P)"
            options={[
              {
                label: 'Yes',
                value: true
              },
              {
                label: 'No',
                value: false
              }
            ]}
            selected={preoperativeAssessmentOrExamIncludingHorP}
            onChange={(value) => {
              setPreoperativeAssessmentOrExamIncludingHorP(value);
              onSave({
                preoperativeAssessmentOrExamIncludingHorP: value
              })
            }}
            error={preoperativeAssessmentOrExamIncludingHorPError}
            disabled={disabled}
          />
        </div>
      </div>
    </form>
  );
});

AdditionalProceduresForm.displayName = 'AdditionalProceduresForm';

AdditionalProceduresForm.propTypes = {
  formData: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  disabled: PropTypes.bool
}

export default AdditionalProceduresForm;